import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined'
import SendIcon from '@mui/icons-material/Send'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { searchRecipients } from 'store/notification/recipients'
import { save as sendNotification } from 'store/notification/notification'
import * as snackbar from 'utils/snackbar'
import { useSession } from 'components/Providers'
import { getGradeLevels } from 'store/lookup'
import RichTextEditor, { htmlToText, isBlank, textToHtml, toEmailHtml } from 'components/RichTextEditor'
import ComposeAssist from './ComposeAssist'
import {
  GRADE_FILTERED_TYPES,
  addressesOf,
  campusesFor,
  enrolmentsFor,
  filterRecipients,
  gradeLevelsFor,
  groupRecipients,
  listOf,
  optedOut,
  pruneSelection,
  reachable,
  sectionsFor,
  sentenceOf,
  studentsOf,
} from './recipients'
import {
  insertToken,
  mergeFieldsFor,
  smsMetrics,
  substitute,
  templateByCode,
  templateLibrary,
  worstCaseRecord,
} from './templates'

const STEPS = ['Recipients', 'Message', 'Review']

const TYPES = [
  { value: 'staff', label: 'Staff' },
  { value: 'parent', label: 'Parents' },
  { value: 'student', label: 'Students' },
]

const MultiSelect = ({ label, options, value, onChange, getLabel = String, disabled, helperText }) => (
  <Autocomplete
    multiple
    disableCloseOnSelect
    size="small"
    limitTags={3}
    disabled={disabled}
    options={options}
    value={value}
    onChange={(_, next) => onChange(next)}
    getOptionLabel={getLabel}
    renderOption={(props, option, { selected }) => (
      <li {...props} key={option}>
        <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
        {getLabel(option)}
      </li>
    )}
    renderInput={params => <TextField {...params} label={label} helperText={helperText} />}
    sx={{ mb: 2 }}
  />
)

export default function SendNotification() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const theme = useTheme()
  const { district } = useSession()
  const isProvider = Boolean(district?.isServiceProvider)

  const [step, setStep] = useState(0)
  const [type, setTypeState] = useState('staff')
  const editorRef = useRef(null)

  const rows = useSelector(state => state.notification.recipients[type])
  const people = useMemo(() => rows && groupRecipients(rows), [rows])
  const loading = useSelector(state => state.notification.recipients.loading[type])
  const failed = useSelector(state => state.notification.recipients.failed?.[type])
  const levelOrder = useSelector(state => state.lookup.gradeLevels)

  const [channel, setChannel] = useState('email')
  const [campuses, setCampuses] = useState([])
  const [gradeLevels, setGradeLevels] = useState([])
  const [gradeSections, setGradeSections] = useState([])

  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [styleCode, setStyleCode] = useState('simple')
  const [sending, setSending] = useState(false)

  useEffect(() => {
    // `failed` stops this retrying forever when the lookup is down
    if (!people && !loading && !failed) dispatch(searchRecipients({ type }))
  }, [dispatch, type, people, loading, failed])

  useEffect(() => {
    if (!levelOrder) dispatch(getGradeLevels())
  }, [dispatch, levelOrder])

  const setType = next => {
    setTypeState(next)
    setCampuses([])
    setGradeLevels([])
    setGradeSections([])
  }

  const changeChannel = next => {
    if (next === channel) return

    setBody(current => {
      if (isBlank(current)) return ''

      return next === 'sms' ? htmlToText(current) : textToHtml(current)
    })
    setChannel(next)
  }

  const hasGrades = GRADE_FILTERED_TYPES.includes(type)
  const styles = useMemo(() => templateLibrary(theme.logos), [theme.logos])

  const campusOptions = useMemo(() => campusesFor(people), [people])

  const gradeOptions = useMemo(
    () => gradeLevelsFor(people, campuses, levelOrder),
    [people, campuses, levelOrder],
  )

  const gradeLabel = useMemo(() => {
    const names = new Map((levelOrder ?? []).map(level => [level?.id, level?.name]))
    return id => names.get(id) ?? String(id)
  }, [levelOrder])

  const sectionOptions = useMemo(
    () => sectionsFor(people, campuses, gradeLevels, levelOrder),
    [people, campuses, gradeLevels, levelOrder],
  )


  const changeCampuses = next => {
    setCampuses(next)

    if (!next.length) {
      setGradeLevels([])
      setGradeSections([])
      return
    }

    const levels = pruneSelection(gradeLevelsFor(people, next, levelOrder), gradeLevels)

    setGradeLevels(levels)
    setGradeSections(current => pruneSelection(sectionsFor(people, next, levels, levelOrder), current))
  }

  const changeGradeLevels = next => {
    setGradeLevels(next)
    setGradeSections(current => pruneSelection(sectionsFor(people, campuses, next, levelOrder), current))
  }

  const selected = useMemo(
    () => filterRecipients(people, { campuses, gradeLevels, gradeSections }),
    [people, campuses, gradeLevels, gradeSections],
  )
  const sendable = useMemo(() => reachable(selected, channel), [selected, channel])
  const optedOutCount = useMemo(
    () => selected.filter(person => optedOut(person, channel)).length,
    [selected, channel],
  )
  const noAddressCount = selected.length - optedOutCount - sendable.length

  const recordFor = (person, address) => {
    const enrolments = enrolmentsFor(person, { campuses, gradeLevels, gradeSections })

    return {
      _id: person.id,
      title: person.title ?? '',
      firstName: person.firstName,
      lastName: person.lastName,
      fullName: [person.firstName, person.lastName].filter(Boolean).join(' '),
      jobTitle: person.jobTitle ?? '',
      studentName: type === 'parent' ? sentenceOf(enrolments.map(enrolment => enrolment.student)) : '',
      students: type === 'parent' ? studentsOf(enrolments, gradeLabel) : '',
      campusName: listOf(enrolments.map(enrolment => enrolment.campus)),
      gradeLevel: listOf(enrolments.map(enrolment => enrolment.gradeLevel && gradeLabel(enrolment.gradeLevel))),
      gradeSection: listOf(enrolments.map(enrolment => enrolment.gradeSection)),
      ...(channel === 'email' ? { recipientEmail: address } : { recipientPhone: address }),
    }
  }

  const records = useMemo(
    () => sendable.flatMap(person => addressesOf(person, channel).map(address => recordFor(person, address))),
    [sendable, channel, type, campuses, gradeLevels, gradeSections, gradeLabel],
  )
  const mergeFields = mergeFieldsFor(type, channel)
  const first = records[0]

  const sms = useMemo(() => smsMetrics(substitute(body, worstCaseRecord(records))), [body, records])

  const previewHtml = useMemo(
    () =>
      channel === 'email'
        ? templateByCode(theme.logos, styleCode).wrap(toEmailHtml(substitute(body || '', first)))
        : '',
    [channel, styleCode, body, first, theme.logos],
  )

  const messageReady = !isBlank(body) && (channel === 'sms' ? !sms.overLimit : subject.trim().length > 0)
  const canAdvance = step === 0 ? sendable.length > 0 : messageReady

  const onSend = async () => {
    setSending(true)
    try {
      const template = {
        [channel]:
          channel === 'email'
            ? { subject, body: templateByCode(theme.logos, styleCode).wrap(toEmailHtml(body)) }
            : { body },
      }

      const result = await dispatch(sendNotification({ channel, template, payload: records }))

      if (result?.error) {
        snackbar.error(result.error?.data?.message || t('Could not send the notification.'))
      } else {
        snackbar.success(t('Notification queued for {{count}} recipients', { count: records.length }))
        setBody('')
        setSubject('')
        setStep(0)
      }
    } finally {
      setSending(false)
    }
  }

  if (!people && loading) return <LinearProgress sx={{ mt: 2 }} />

  if (failed) {
    return (
      <Alert
        severity="error"
        sx={{ mt: 2 }}
        action={
          <Button size="small" onClick={() => dispatch(searchRecipients({ type }))}>
            {t('Retry')}
          </Button>
        }
      >
        {t('Could not load recipients.')}
      </Alert>
    )
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map(label => (
            <Step key={label}>
              <StepLabel>{t(label)}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={700} color="primary.main">
            {sendable.length.toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('recipients')} · {t(TYPES.find(option => option.value === type)?.label ?? '')} ·{' '}
            {channel === 'sms' ? t('SMS') : t('Email')}
          </Typography>
          {noAddressCount > 0 && (
            <Typography variant="caption" color="warning.main">
              {t('{{count}} have no address', { count: noAddressCount })}
            </Typography>
          )}
          {optedOutCount > 0 && (
            <Typography variant="caption" color="warning.main">
              {channel === 'sms'
                ? t('{{count}} have SMS notification off', { count: optedOutCount })
                : t('{{count}} have email notification off', { count: optedOutCount })}
            </Typography>
          )}
        </Stack>

        <Divider sx={{ mb: 3 }} />

        {step === 0 && (
          <Box sx={{ maxWidth: 560 }}>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={channel}
              onChange={(_, next) => next && changeChannel(next)}
              sx={{ mb: 2 }}
            >
              <ToggleButton value="email">
                <EmailOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
                {t('Send Email')}
              </ToggleButton>
              <ToggleButton value="sms" disabled={type === 'student'}>
                <SmsOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
                {t('Send SMS')}
              </ToggleButton>
            </ToggleButtonGroup>

            <TextField
              select
              fullWidth
              size="small"
              label={t('Recipient Type')}
              value={type}
              onChange={event => setType(event.target.value)}
              sx={{ mb: 2 }}
            >
              {TYPES.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {t(option.label)}
                </MenuItem>
              ))}
            </TextField>

            {isProvider && (
              <MultiSelect label={t('Campus')} options={campusOptions} value={campuses} onChange={changeCampuses} />
            )}

            {hasGrades && (
              <>
                <MultiSelect
                  label={t('Grade Level')}
                  options={gradeOptions}
                  value={gradeLevels}
                  onChange={changeGradeLevels}
                  getLabel={gradeLabel}
                  disabled={isProvider && !campuses.length}
                  helperText={isProvider && !campuses.length ? t('Choose a campus first') : undefined}
                />
                {!isProvider && (
                  <MultiSelect
                    label={t('Grade Section')}
                    options={sectionOptions}
                    value={gradeSections}
                    onChange={setGradeSections}
                  />
                )}
              </>
            )}

            <Typography variant="caption" color="text.secondary">
              {t('Leave a filter empty to include everyone.')}
            </Typography>
          </Box>
        )}

        {step === 1 && (
          <Box>
            {channel === 'email' && (
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('Subject')}
                  value={subject}
                  onChange={event => setSubject(event.target.value)}
                />
                <TextField
                  select
                  size="small"
                  label={t('Style')}
                  value={styleCode}
                  onChange={event => setStyleCode(event.target.value)}
                  helperText={styles.find(style => style.code === styleCode)?.description}
                  sx={{ minWidth: 220 }}
                >
                  {styles.map(style => (
                    <MenuItem key={style.code} value={style.code}>
                      {t(style.name)}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            )}

            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {channel === 'email' ? t('Body') : t('Message')}
              </Typography>
              <ComposeAssist
                body={body}
                channel={channel}
                onApply={next => {
                  setBody(next)
                  if (channel === 'email') editorRef.current?.focus()
                }}
              />
            </Stack>

            {channel === 'email' ? (
              <RichTextEditor
                ref={editorRef}
                value={body}
                onChange={setBody}
                placeholder={t('Write your message…')}
              />
            ) : (
              <TextField
                fullWidth
                multiline
                minRows={4}
                value={body}
                onChange={event => setBody(event.target.value)}
                error={sms.overLimit}
                helperText={
                  t('{{length}} / {{limit}} characters with fields filled in — one message per recipient', {
                    length: sms.length,
                    limit: sms.limit,
                  }) + (sms.unicode ? ` · ${t('Unicode characters reduce the limit')}` : '')
                }
              />
            )}

            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1.5, gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, alignSelf: 'center' }}>
                {t('Insert field')}:
              </Typography>
              {mergeFields.map(field => (
                <Chip
                  key={field.token}
                  size="small"
                  variant="outlined"
                  label={t(field.label)}
                  onClick={() =>
                    channel === 'email'
                      ? editorRef.current?.insertText(`{{${field.token}}}`)
                      : setBody(insertToken(body, field.token))
                  }
                />
              ))}
            </Stack>
          </Box>
        )}

        {step === 2 && (
          <Box>
            {first && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {t('as your first recipient - {{name}} will see it', { name: first.fullName || first.recipientEmail })}
              </Typography>
            )}

            {channel === 'email' ? (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <Box component="span" sx={{ color: 'text.secondary' }}>
                    {t('Subject')}:{' '}
                  </Box>
                  {substitute(subject, first)}
                </Typography>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    maxHeight: 420,
                    overflow: 'auto',
                    bgcolor: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.9) : 'background.paper',
                  }}
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              </>
            ) : (
              <Paper
                variant="outlined"
                sx={{ p: 2, borderRadius: 2, maxWidth: 380, whiteSpace: 'pre-wrap', bgcolor: 'action.hover' }}
              >
                <Typography variant="body2">{substitute(body, first)}</Typography>
              </Paper>
            )}

            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('This sends immediately to {{count}} recipients and cannot be undone.', {
                count: records.length,
              })}
            </Alert>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        <Stack direction="row" justifyContent="space-between">
          <Button
            startIcon={<ArrowBackIcon />}
            disabled={step === 0 || sending}
            onClick={() => setStep(current => current - 1)}
          >
            {t('Back')}
          </Button>

          {step < STEPS.length - 1 ? (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              disabled={!canAdvance}
              onClick={() => setStep(current => current + 1)}
            >
              {t('Next')}
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              disabled={sending || !records.length}
              onClick={onSend}
            >
              {t('Send')}
            </Button>
          )}
        </Stack>
      </Paper>

    </Box>
  )
}
